import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router";
import { lookupGuestBooking } from "../../api/booking.api";
import type { Booking } from "@abejon/shared";

type GuestBooking = Booking & {
  serviceName: string;
  businessName: string;
  businessSlug: string;
};

function formatTime(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${m.toString().padStart(2, "0")} ${period}`;
}

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}min` : `${hrs}h`;
}

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  confirmed: { bg: "bg-blue-50", text: "text-blue-700", label: "Confirmed" },
  completed: { bg: "bg-green-50", text: "text-green-700", label: "Completed" },
  cancelled_by_business: { bg: "bg-red-50", text: "text-red-700", label: "Cancelled by business" },
  cancelled_by_customer: { bg: "bg-orange-50", text: "text-orange-700", label: "Cancelled" },
  no_show: { bg: "bg-gray-100", text: "text-gray-600", label: "No-show" },
};

export function LookupPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [code, setCode] = useState(searchParams.get("code") ?? "");
  const [bookings, setBookings] = useState<GuestBooking[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Auto-lookup when arriving via email link with both params
  useEffect(() => {
    const paramEmail = searchParams.get("email");
    const paramCode = searchParams.get("code");
    if (paramEmail && paramCode) {
      lookup(paramEmail, paramCode);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function lookup(lookupEmail: string, lookupCode: string) {
    setLoading(true);
    setError("");
    setBookings(null);
    try {
      const result = await lookupGuestBooking(lookupEmail, lookupCode);
      setBookings(result.bookings);
      if (result.bookings.length === 0) {
        setError("No bookings found with that email and code.");
      }
    } catch {
      setError("Could not find bookings. Please check your email and code.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await lookup(email, code);
  }

  return (
    <div className="mx-auto max-w-lg py-8 px-4">
      <h1 className="mb-2 text-2xl font-bold">Look Up Booking</h1>
      <p className="mb-6 text-sm text-gray-500">
        Enter the email and lookup code from your booking confirmation.
      </p>

      <form onSubmit={handleSubmit} className="mb-6 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="your@email.com"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Lookup Code
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
            maxLength={6}
            placeholder="ABC123"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono tracking-widest uppercase focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {loading ? "Looking up..." : "Find Booking"}
        </button>
      </form>

      {error && (
        <p className="mb-4 text-sm text-red-600">{error}</p>
      )}

      {bookings && bookings.length > 0 && (
        <div className="space-y-3">
          {bookings.map((booking) => {
            const style = STATUS_STYLES[booking.status] ?? STATUS_STYLES.confirmed;

            return (
              <div
                key={booking.id}
                className="rounded-lg border border-gray-200 p-4"
              >
                <div className="flex items-center gap-2">
                  <Link
                    to={`/b/${booking.businessSlug}`}
                    className="text-sm font-semibold text-gray-900 hover:underline"
                  >
                    {booking.businessName}
                  </Link>
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}>
                    {style.label}
                  </span>
                </div>

                <p className="mt-1 text-sm text-gray-700">{booking.serviceName}</p>

                <p className="mt-0.5 text-sm text-gray-500">
                  {formatDate(booking.date)} · {formatTime(booking.startTime)} – {formatTime(booking.endTime)}
                </p>

                <p className="mt-1 text-xs text-gray-400">
                  {formatDuration(booking.durationMinutes)} · {formatPrice(booking.priceInCents)}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
